import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Nav from '../../components/Nav';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import {
  Page, Wrap, Card, Step, Title, Lede,
  DataContract, DataCol, NoWaste, Reassurance,
  BadgeStrip, Badge,
  ProfileSection, ProfileHeading, ProfileRow, ProfileField,
  ProviderRow, ProviderBtn, Actions, SmallNote, Spinner,
  ConsentRow, ConsentError,
} from './styles';

const Connect = () => {
  const navigate = useNavigate();
  const [provider, setProvider] = useState('fortnox');
  const [connecting, setConnecting] = useState(false);
  const [vismaNotice, setVismaNotice] = useState(false);
  const [consent, setConsent] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [industry, setIndustry] = useState('konsult');
  const [employees, setEmployees] = useState(5);

  const start = () => {
    if (!consent) {
      setShowConsentError(true);
      return;
    }
    if (provider === 'fortnox') {
      setConnecting(true); // spinner medan webbläsaren navigerar till OAuth
      const params = new URLSearchParams({
        industry,
        employees: String(employees),
      });
      window.location.href = `/api/fortnox/auth?${params}`;
      return;
    }
    // Visma-OAuth är inte byggd än — vi simulerar ALDRIG en koppling.
    // Registrera intresset och var ärlig med läget.
    fetch('/api/waitlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: 'visma_connect', industry, employees }),
    }).catch(() => {});
    setVismaNotice(true);
  };

  return (
    <Page>
      <Nav variant="public" />
      <Wrap>
        <Card>
          <Step><span className="dot" /> Steg 1 av 3 · Anslut bokföring</Step>
          <Title>Koppla din bokföring</Title>

          <Lede>
            60 sekunders koppling via Fortnox eller Visma — och du kan stänga av den lika snabbt.
          </Lede>

          <DataContract>
            <DataCol $allow>
              <span className="head"><div className="dot">✓</div> Vi läser</span>
              <ul>
                <li><Icon name="check" size={14} stroke={2.4} /> Leverantörsfakturor (konton 4xxx–7xxx)</li>
                <li><Icon name="check" size={14} stroke={2.4} /> Avtalskategorier &amp; förfallodatum</li>
                <li><Icon name="check" size={14} stroke={2.4} /> Belopp &amp; betalningshistorik</li>
              </ul>
            </DataCol>
            <DataCol>
              <span className="head"><div className="dot">✗</div> Vi läser inte</span>
              <ul>
                <li><Icon name="lock" size={14} stroke={2} /> Kundfakturor &amp; intäkter</li>
                <li><Icon name="lock" size={14} stroke={2} /> Lönedata &amp; personnummer</li>
                <li><Icon name="lock" size={14} stroke={2} /> Bankkonton &amp; kassaflöde</li>
              </ul>
            </DataCol>
          </DataContract>

          <NoWaste>
            <div className="icon"><Icon name="check" size={16} stroke={2.4} /></div>
            <div>
              {/* Här stod «Då är ditt bolag redan optimerat. Vi raderar Fortnox-kopplingen och all din data
                  automatiskt». Ingen kod raderar något efter 30 dagar, analyser arkiveras och raderas aldrig
                  (grundarbeslut 2026-09-11), och «optimerat» är ett omdöme vi inte mätt (kundmeningsregistret). */}
              <strong>Vårt löfte — hittar vi inga överpriser?</strong>
              <span>Då har du inte betalat en krona. Kopplingen tar du bort när du vill, direkt i Fortnox.</span>
            </div>
          </NoWaste>

          <ProfileSection>
            <ProfileHeading>Berätta lite om bolaget</ProfileHeading>
            <ProfileRow>
              <ProfileField>
                <span className="label">Bransch</span>
                <select value={industry} onChange={(e) => setIndustry(e.target.value)}>
                  <option value="ehandel">E-handel &amp; Detaljhandel</option>
                  <option value="tillverkning">Industri &amp; Tillverkning</option>
                  <option value="it-tech">IT, Tech &amp; Mjukvara</option>
                  <option value="bygg">Bygg, Hantverk &amp; Fastighet</option>
                  <option value="hotell">Hotell, Restaurang &amp; Event</option>
                  <option value="konsult">Konsult &amp; Företagstjänster</option>
                  <option value="transport">Transport &amp; Logistik</option>
                  <option value="vard">Vård, Omsorg &amp; Hälsa</option>
                  <option value="ovrigt">Övrigt / Annan bransch</option>
                </select>
              </ProfileField>
              <ProfileField>
                <span className="label">Antal anställda</span>
                <input
                  type="number"
                  min="1"
                  max="5000"
                  value={employees}
                  onChange={(e) => setEmployees(Number(e.target.value))}
                />
              </ProfileField>
            </ProfileRow>
          </ProfileSection>

          <ProviderRow>
            <ProviderBtn $active={provider === 'fortnox'} onClick={() => setProvider('fortnox')}>
              <span className="badge">Vanligast</span>
              <Icon name="fortnox" size={22} color="#0F5132" />
              <strong>Fortnox</strong>
              <span>Direkt OAuth-koppling</span>
            </ProviderBtn>
            <ProviderBtn $active={provider === 'visma'} onClick={() => setProvider('visma')}>
              <span className="badge">Inom kort</span>
              <Icon name="fortnox" size={22} color="#0F5132" />
              <strong>Visma eEkonomi</strong>
              <span>Lanseras inom kort</span>
            </ProviderBtn>
          </ProviderRow>

          <BadgeStrip>
            <Badge>
              <div className="icon"><Icon name="bankid" size={16} stroke={2} /></div>
              <strong>BankID</strong>
              <span>Säker identifiering</span>
            </Badge>
            <Badge>
              <div className="icon"><Icon name="shield" size={16} stroke={2} /></div>
              <strong>GDPR</strong>
              {/* RÄTTAT 2026-08-07: "Fullt regelefterlevnad" är en självcertifiering ingen kan
                  kontrollera — och den stod bredvid en badge som samtidigt påstod fel land för
                  datan. Det vi FAKTISKT kan visa är dokumenten. */}
              <span>Biträdesavtal &amp; registerförteckning publicerade</span>
            </Badge>
            <Badge>
              <div className="icon"><Icon name="lock" size={16} stroke={2} /></div>
              <strong>AES-256</strong>
              <span>Krypterad i vila & i transport</span>
            </Badge>
            {/* RÄTTAT 2026-08-07: badgen sa "Data hos Bahnhof, Stockholm". Bahnhof är planerat,
                inte i drift — biträdena är i dag Neon, Vercel, Anthropic och Resend, under SCC.
                En trygghetsbadge som påstår fel land är värre än ingen badge alls. */}
            <Badge>
              <div className="icon"><Icon name="check" size={16} stroke={2.2} /></div>
              <strong>EU/EES + SCC</strong>
              <span>Varje biträde namngivet i policyn</span>
            </Badge>
          </BadgeStrip>

          {/* BORTTAGEN 2026-08-07: här stod "1 247 leverantörsfakturor analyserade denna vecka"
              med en pulserande live-prick. Talet var påhittat — hela systemet har analyserat 38
              fakturor sedan start. En falsk socialt-bevis-räknare på den yta där kunden ska
              lämna ifrån sig bokföringsåtkomst är det dyraste stället vi kunde ljuga på.
              Ingen ersättningssiffra: vi har inget volymbevis än, och då säger vi inget (regel 3).
              Återinförs ENDAST kopplad mot en verklig räkning ur invoice_analyses. */}

          <ConsentRow $error={showConsentError && !consent}>
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => {
                setConsent(e.target.checked);
                if (e.target.checked) setShowConsentError(false);
              }}
              aria-describedby="consent-text"
            />
            <span className="text" id="consent-text">
              Jag accepterar <Link to="/villkor">de allmänna villkoren</Link> och{' '}
              <Link to="/integritet">integritetspolicyn</Link> och bekräftar att jag
              har behörighet att utfärda fullmakt för företaget.
            </span>
          </ConsentRow>
          {showConsentError && !consent && (
            <ConsentError>
              <Icon name="lock" size={12} stroke={2.4} />
              Du måste godkänna villkoren innan du går vidare.
            </ConsentError>
          )}

          {vismaNotice && (
            <ConsentError as="div" style={{ background: 'rgba(27,122,110,0.08)', color: '#1B7A6E' }}>
              <Icon name="check" size={12} stroke={2.4} />
              Visma-kopplingen lanseras inom kort — vi har noterat ert intresse och hör av oss.
              Tills dess: <Link to="/testa-faktura" style={{ color: '#1B7A6E', fontWeight: 600 }}>analysera en faktura direkt</Link>.
            </ConsentError>
          )}

          <Actions>
            <Button $variant="gradient" $size="lg" onClick={start} disabled={connecting} $full>
              {connecting ? (
                <>
                  <Spinner /> Ansluter till {provider === 'fortnox' ? 'Fortnox' : 'Visma'}…
                </>
              ) : (
                <>
                  Anslut {provider === 'fortnox' ? 'Fortnox' : 'Visma'} <Icon name="arrow" size={18} />
                </>
              )}
            </Button>
            <Reassurance>
              <Icon name="lock" size={12} stroke={2.2} />
              Du skickas nu till {provider === 'fortnox' ? 'Fortnox' : 'Visma'} för att godkänna läsåtkomst. Inga ändringar görs i din bokföring.
            </Reassurance>
            <Button $variant="ghost" $size="md" onClick={() => navigate('/')}>
              Tillbaka
            </Button>
          </Actions>

          <SmallNote>
            Läs <Link to="/villkor" style={{ textDecoration: 'underline' }}>allmänna villkoren</Link>,
            vår <Link to="/integritet" style={{ textDecoration: 'underline' }}>integritetspolicy</Link>{' '}
            och <Link to="/cookies" style={{ textDecoration: 'underline' }}>cookie-policy</Link>.
          </SmallNote>
        </Card>
      </Wrap>
    </Page>
  );
};

export default Connect;
